import React, { useEffect, useMemo, useRef, useState } from "react";
import { CalendarDays, ChevronDown, Clock, Plus, Trash2 } from "lucide-react";
import { Toaster } from "../../../common/Toaster/Toaster";
import { FilterChip } from "../../../common/comms/Chip";
import { EmptyState } from "../../../common/comms/EmptyState";
import { Modal, useModalExit } from "../../../common/comms/Modal";
import { RetryBanner } from "../../../common/comms/RetryBanner";
import { SkeletonRows } from "../../../common/comms/SkeletonRows";
import { SlotMonthCalendar } from "./SlotMonthCalendar";
import { SlotPreviewLegend, SlotPreviewList } from "./SlotPreviewList";
import {
  buildWeek,
  callTierList,
  formatTimeOfDay,
  parseStrandedBookings,
  countDaySlots,
  formatDayHeading,
  formatMinutesRange,
  formatWeekRange,
  nextShiftStart,
  shiftSlotsOnDate,
  shiftsFrom,
  shiftTimeOptions,
  summariseShift,
  validateShiftDraft,
  weekdayName,
} from "./slotGrid";

/** Where a department's call types are set (`CallTypesSection`), as the sidebar names it. */
const CALL_TYPES_PAGE = {
  Nutritionist: "Nutrition → References (Diet Utils)",
  Fitness: "Fitness → References (Workout Utils)",
};

const ALL_DAYS = [1, 2, 3, 4, 5, 6, 7];
const WEEKDAYS = [1, 2, 3, 4, 5];
const SHORT_DAY = (dayNumber) => weekdayName(dayNumber).slice(0, 3);

const WEEKDAY_NAME = (date) => date.toLocaleDateString("en-GB", { weekday: "long" });
const LONG_DATE = (date) =>
  date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
/** "Wed 30 Sept" — the date a shift change applies from. */
const FROM_DATE = (date) =>
  date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });

/**
 * A coach's weekly shifts, one row per day of the chosen week.
 *
 * Shifts are weekday + time and **repeat every week while in effect**
 * (`be3e423`): each row shows the shifts in effect on its own date
 * (`shiftsForDate`), so a week straddling a change of hours shows the old
 * hours before the change and the new ones after. The copy says shifts
 * repeat, because a manager picking next week and seeing this week's shifts
 * would otherwise wonder whether they have to add them again.
 *
 * Nothing bookable is stored: the server generates slots from these shifts and
 * the department's call types every time a client asks. So the "N slots" on a
 * row and the View slots dialog are the same generation done here
 * (`countDaySlots`, `shiftSlotsOnDate` in slotGrid), labelled as a preview.
 *
 * A controlled panel: shifts, call types and bookings come from
 * `BookingsWorkspace`; the week is the workspace's too, so it survives a trip
 * to the Bookings sub-tab.
 *
 * **Editing** (a manager on their own or a report's calendar, an executive on
 * anyone's): Add shift puts one time window on one or more weekdays; a shift's
 * trash icon removes it — always a close, so it stays on record
 * (`planShiftRemoval`), and refused while it holds any upcoming call. To
 * change hours, remove it and add the new window.
 * **A new shift starts at its next run** (each weekday's first start time
 * still ahead), and **a removal applies from the moment it is saved**,
 * whichever row either was made on; everything before keeps what it had.
 *
 * @param {Object} props
 * @param {ReturnType<import("./useCoachShifts").useCoachShifts>} props.shifts
 * @param {Record<string, Array<Object>>} props.bookingsByDate - This calendar's
 *   bookings for the displayed week, keyed `YYYY-MM-DD`, carrying `startsAt`.
 * @param {Date} props.weekDate - Any day in the week on screen.
 * @param {(date: Date) => void} props.onWeekChange
 * @param {boolean} [props.readOnly=true] - No way of changing shifts.
 * @param {string} [props.title="My shifts"]
 * @param {string} [props.coachName] - Named in empty states when viewing someone else.
 * @param {string[]|null} [props.departmentChoices] - When set, a switch for
 *   which department's call types the slot counts use (an Executive coach,
 *   whose department each member's subscription decides).
 * @param {(title: string) => void} [props.onDepartmentChange]
 */
export const MyShiftsTab = ({
  shifts,
  bookingsByDate,
  weekDate,
  onWeekChange,
  readOnly = true,
  title = "My shifts",
  coachName,
  departmentChoices,
  onDepartmentChange,
}) => {
  const today = useMemo(() => new Date(), []);
  const week = useMemo(() => buildWeek(weekDate, today), [weekDate, today]);
  const isThisWeek = week.some((day) => day.isToday);
  const hasPastDays = week.some((day) => day.isPast);
  const [viewing, setViewing] = useState(null);
  // null, or { mode: "add", dayNumbers, fixedDay? }.
  const [form, setForm] = useState(null);
  // null, or { shift } — the chip whose trash icon was pressed.
  const [removing, setRemoving] = useState(null);
  const [toast, setToast] = useState(null);
  const listRef = useRef(null);

  const {
    shiftsForDate,
    hasCurrentShifts,
    tiers,
    hasCallTypes,
    callTypesReady,
    offsetMinutes,
    departmentTitle,
    hasData,
    busy,
    error,
    refresh,
    canWrite,
  } = shifts;

  const viewingOwnCalendar = !coachName;
  const editable = !readOnly && canWrite;

  const announce = (message) => setToast({ message, type: "success", id: Date.now() });

  return (
    <div className="space-y-4">
      {error ? (
        <RetryBanner
          message={error.message || "Something went wrong loading shifts."}
          onRetry={refresh}
        />
      ) : null}

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-bc-text">{title}</h2>
          <p className="mt-0.5 text-xs text-bc-soft">
            Slots are generated automatically from these shifts. Shifts repeat every week
            until changed. A new shift starts at its next start time, or right away if
            today's is under way; a removal applies
            from the moment it's saved.
          </p>
          {readOnly && viewingOwnCalendar ? (
            <p className="mt-0.5 text-xs text-bc-soft">Your manager sets your shifts.</p>
          ) : null}
          {editable && hasPastDays ? (
            <p className="mt-0.5 text-xs text-bc-soft">
              Past days can't be edited — change a shift from the day it should change on.
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-3">
          {isThisWeek ? <span className="text-xs text-bc-soft">This week</span> : null}
          {departmentChoices?.length ? (
            <label className="flex items-center gap-2 text-xs text-bc-soft">
              Slots as
              <select
                value={departmentTitle || ""}
                onChange={(event) => onDepartmentChange?.(event.target.value)}
                className="min-h-[40px] rounded-lg border border-gray-200 bg-white px-2 text-sm text-bc-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary"
              >
                {departmentChoices.map((choice) => (
                  <option key={choice} value={choice}>
                    {choice === "Nutritionist" ? "Diet coach" : `${choice} coach`}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <WeekPicker week={week} onPick={onWeekChange} />
          {editable ? (
            <button
              type="button"
              onClick={() => setForm({ mode: "add", dayNumbers: [] })}
              className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg bg-orange-primary px-4 text-sm font-semibold text-white transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-1"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Add shift
            </button>
          ) : null}
        </div>
      </div>

      {!readOnly && !canWrite && hasData ? (
        <p className="rounded-lg border border-bc-hairline bg-light-gray/40 px-4 py-2.5 text-xs text-bc-text-2">
          Shift editing isn't available right now — the weekday list didn't load. Sign out
          and back in, then try again.
        </p>
      ) : null}

      {callTypesReady && !hasCallTypes ? (
        <p className="rounded-lg border border-bc-hairline bg-light-gray/40 px-4 py-2.5 text-xs text-bc-text-2">
          {departmentTitle
            ? `No call types are set for ${departmentTitle} yet, so slot counts can't be worked out. An executive sets them under ${
                CALL_TYPES_PAGE[departmentTitle] || "Diet Utils / Workout Utils"
              } → Call types.`
            : "No call types are set for this department yet, so slot counts can't be worked out."}
        </p>
      ) : null}

      {!hasData && busy ? (
        <SkeletonRows count={7} />
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <ul ref={listRef}>
            {week.map((day) => {
              const dayShifts = shiftsForDate(day.date);
              const count = countDaySlots(dayShifts, tiers, offsetMinutes, day.date);
              // A change applies from the row's date on, and a date that has
              // gone is not one to change from — lock the row instead.
              const rowEditable = editable && !day.isPast;
              return (
                <li
                  key={day.key}
                  className={`flex flex-wrap items-center gap-3 border-b border-gray-100 px-4 py-3 last:border-b-0 sm:flex-nowrap ${
                    day.isToday ? "border-l-4 border-l-orange-primary bg-light-orange/30" : ""
                  }`}
                  aria-current={day.isToday ? "date" : undefined}
                >
                  <div className="w-36 flex-shrink-0">
                    <p
                      className={`text-sm font-semibold ${
                        day.isToday ? "text-orange-primary" : "text-bc-text"
                      }`}
                    >
                      {WEEKDAY_NAME(day.date)}
                    </p>
                    <p className="text-[11px] text-bc-soft">
                      {day.isToday ? "Today, " : ""}
                      {LONG_DATE(day.date)}
                    </p>
                  </div>

                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                    {dayShifts.length === 0 && !rowEditable ? (
                      <span className="text-xs text-bc-faint">No shifts</span>
                    ) : null}
                    {dayShifts.map((shift) => {
                      const label = formatMinutesRange(shift.startMinutes, shift.endMinutes);
                      return (
                        <span
                          key={shift.key}
                          className="inline-flex min-h-[32px] items-center gap-1.5 rounded-full border border-orange-primary/20 bg-light-orange/60 px-3 text-xs font-semibold text-bc-text"
                        >
                          {label}
                          {rowEditable ? (
                            <button
                              type="button"
                              onClick={() => setRemoving({ shift })}
                              aria-label={`Remove ${weekdayName(day.dayNumber)} ${label} shift`}
                              className="-mr-1.5 inline-flex h-6 w-6 items-center justify-center rounded-full text-bc-faint transition-colors hover:bg-light-orange hover:text-danger-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary"
                            >
                              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                            </button>
                          ) : null}
                        </span>
                      );
                    })}
                    {rowEditable ? (
                      <button
                        type="button"
                        // Locked to this row's day — the header's Add shift is
                        // where several days are picked at once.
                        onClick={() =>
                          setForm({
                            mode: "add",
                            dayNumbers: [day.dayNumber],
                            fixedDay: true,
                          })
                        }
                        aria-label={`Add a shift on ${weekdayName(day.dayNumber)}`}
                        className="inline-flex min-h-[32px] items-center gap-1 rounded-full border border-dashed border-gray-300 px-3 text-xs font-semibold text-bc-soft transition-colors hover:border-orange-primary hover:text-orange-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary"
                      >
                        <Plus className="h-3 w-3" aria-hidden="true" />
                        Add
                      </button>
                    ) : null}
                  </div>

                  <div className="ml-auto flex flex-shrink-0 items-center gap-4">
                    <span
                      className="text-[11px] text-bc-faint"
                      title={count === null ? undefined : "Regular-call slots. First calls are longer, so fewer fit."}
                    >
                      {count === null ? "—" : `${count} slot${count === 1 ? "" : "s"}`}
                    </span>
                    <button
                      type="button"
                      onClick={() => setViewing(day)}
                      disabled={dayShifts.length === 0 || !hasCallTypes}
                      aria-label={`View slots for ${WEEKDAY_NAME(day.date)} ${LONG_DATE(day.date)}`}
                      className="min-h-[32px] rounded-lg px-2 text-xs font-semibold text-bc-text-2 transition-colors hover:bg-light-gray/60 disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary"
                    >
                      View slots
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {hasData && !hasCurrentShifts ? (
        <EmptyState
          title="No shifts yet"
          hint={
            viewingOwnCalendar
              ? readOnly
                ? "You have no shifts, so clients can't book calls with you yet. Ask your manager to add them."
                : "Add a shift to open your calendar for calls. It repeats every week."
              : `${coachName} has no shifts, so clients can't book calls with them yet.`
          }
        />
      ) : null}

      <ViewSlotsDialog
        day={viewing}
        dayShifts={viewing ? shiftsForDate(viewing.date) : []}
        bookings={viewing ? bookingsByDate?.[viewing.key] || [] : []}
        shifts={shifts}
        onClose={() => setViewing(null)}
      />

      {form ? (
        <ShiftFormDialog
          // Remounted per opening so a half-typed draft never carries over.
          key={`add-${form.dayNumbers.join("")}`}
          form={form}
          shifts={shifts}
          onClose={() => setForm(null)}
          onDone={(message) => {
            setForm(null);
            announce(message);
          }}
        />
      ) : null}

      {removing ? (
        <RemoveShiftDialog
          key={removing.shift.key}
          shift={removing.shift}
          shifts={shifts}
          onClose={() => setRemoving(null)}
          onDone={(message) => {
            const addLabel = `Add a shift on ${weekdayName(removing.shift.dayNumber)}`;
            setRemoving(null);
            announce(message);
            // The trash button that opened the dialog is gone with its chip, so
            // focus goes to the row's Add instead of falling back to the page.
            requestAnimationFrame(() =>
              listRef.current?.querySelector(`[aria-label="${addLabel}"]`)?.focus()
            );
          }}
        />
      ) : null}

      {toast?.message ? <Toaster key={toast.id} message={toast.message} type={toast.type} /> : null}
    </div>
  );
};

/**
 * Add a shift to one or more weekdays, each **from its next run** — the first
 * date of that weekday whose start time is still ahead — whichever row it was
 * opened from.
 *
 * Validates what the server would refuse (no days, end before start, overlap
 * with the coach's other shifts that day still in effect) before sending, and
 * on a refusal stays open with the API's own words. Since `bd2e283` an ADD is
 * only ever refused for overlap (400), never for booked calls. The summary
 * line runs the same slot generator View slots does, so "fits 7 regular
 * slots" is what clients will be offered.
 */
const ShiftFormDialog = ({ form, shifts, onClose, onDone }) => {
  // Opened from one day's row: that day only, with no day picker.
  const singleDay = Boolean(form.fixedDay);
  const [dayNumbers, setDayNumbers] = useState(form.dayNumbers);
  const [startMinutes, setStartMinutes] = useState(9 * 60);
  const [endMinutes, setEndMinutes] = useState(12 * 60);
  const [error, setError] = useState(null);

  const { localShifts, tiers, offsetMinutes, saving } = shifts;
  // Animates in and out; nothing closes it while the ADD is in flight.
  const { leaving, close, leaveThen } = useModalExit({ onClose, blocked: saving });

  const startOptions = useMemo(() => shiftTimeOptions("start"), []);
  const endOptions = useMemo(() => shiftTimeOptions("end"), []);

  const draft = { dayNumbers, startMinutes, endMinutes };
  const now = new Date();
  const problem = validateShiftDraft(draft, shiftsFrom(localShifts, now, startMinutes, endMinutes), undefined, now);
  const firstRun = singleDay ? nextShiftStart(now, dayNumbers[0], startMinutes, endMinutes) : null;
  // Today's window already ended: today's weekday first runs a week from now.
  const todayNumber = ((now.getDay() + 6) % 7) + 1;
  const todayFirstRun = dayNumbers.includes(todayNumber)
    ? nextShiftStart(now, todayNumber, startMinutes, endMinutes)
    : null;
  const skipsToday = Boolean(todayFirstRun) && todayFirstRun.getDate() !== now.getDate();
  const summary =
    endMinutes > startMinutes
      ? summariseShift({ dayNumber: dayNumbers[0] || 1, startMinutes, endMinutes }, tiers, offsetMinutes)
      : null;

  const toggleDay = (dayNumber) =>
    setDayNumbers((current) =>
      current.includes(dayNumber)
        ? current.filter((value) => value !== dayNumber)
        : [...current, dayNumber].sort((a, b) => a - b)
    );

  // `saving` disables the button only from the next render; this catches a
  // second click landing before it, which would send the ADD twice.
  const submittingRef = useRef(false);
  const run = async (action, message) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setError(null);
    try {
      await action();
      leaveThen(() => onDone(message));
    } catch (err) {
      setError(err?.message || "That didn't save. Try again.");
    } finally {
      submittingRef.current = false;
    }
  };

  const save = () => {
    if (problem) {
      setError(problem);
      return;
    }
    const range = formatMinutesRange(startMinutes, endMinutes);
    run(
      () => shifts.addShifts(draft),
      `${range} added to ${dayNumbers.length} day${dayNumbers.length === 1 ? "" : "s"}.`
    );
  };

  const selectClass =
    "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-bc-text focus:outline-none focus:ring-2 focus:ring-orange-primary/40";
  const linkClass =
    "text-xs font-semibold text-orange-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary";
  const hours = summary ? summary.minutes / 60 : 0;

  return (
    <Modal
      isOpen
      onClose={close}
      animated
      leaving={leaving}
      closeDisabled={saving}
      title="Add shift"
      subtitle={singleDay ? weekdayName(dayNumbers[0]) : "Pick one or more days"}
      footer={
        <>
          <button
            type="button"
            onClick={close}
            disabled={saving}
            className="min-h-[40px] rounded-lg border border-gray-200 px-4 text-sm font-semibold text-bc-text-2 hover:bg-light-gray/60 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || Boolean(problem)}
            className="min-h-[40px] rounded-lg bg-orange-primary px-4 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {saving
              ? "Saving…"
              : singleDay
              ? `Add to ${weekdayName(dayNumbers[0])}`
              : dayNumbers.length
              ? `Add to ${dayNumbers.length} day${dayNumbers.length === 1 ? "" : "s"}`
              : "Add shift"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {!singleDay ? (
          <fieldset>
            <div className="mb-2 flex items-center justify-between">
              <legend className="text-xs font-semibold text-bc-text-2">Days</legend>
              <span className="flex gap-3">
                <button type="button" className={linkClass} onClick={() => setDayNumbers(WEEKDAYS)}>
                  Mon–Fri
                </button>
                <button type="button" className={linkClass} onClick={() => setDayNumbers(ALL_DAYS)}>
                  All days
                </button>
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {ALL_DAYS.map((dayNumber) => (
                <FilterChip
                  key={dayNumber}
                  label={SHORT_DAY(dayNumber)}
                  active={dayNumbers.includes(dayNumber)}
                  onClick={() => toggleDay(dayNumber)}
                />
              ))}
            </div>
          </fieldset>
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-bc-text-2">Start time</span>
            <select
              value={startMinutes}
              onChange={(event) => setStartMinutes(Number(event.target.value))}
              className={selectClass}
            >
              {startOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-bc-text-2">End time</span>
            <select
              value={endMinutes}
              onChange={(event) => setEndMinutes(Number(event.target.value))}
              className={selectClass}
            >
              {endOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <p className="flex items-start gap-2 rounded-lg bg-light-gray/50 px-3 py-2 text-xs text-bc-text-2">
          <Clock className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-bc-faint" aria-hidden="true" />
          <span>
            {summary
              ? `${Number.isInteger(hours) ? hours : hours.toFixed(1)} h shift${
                  summary.counts.length
                    ? ` · fits about ${summary.counts
                        .map((count) => `${count.count} ${count.short}`)
                        .join(" · ")} slots`
                    : " · set call types to see how many slots it fits"
                }`
              : "Pick an end time after the start."}
            {" · "}
            {firstRun
              ? `Repeats every ${weekdayName(dayNumbers[0])} from ${FROM_DATE(firstRun)}, ${formatTimeOfDay(
                  firstRun.getHours() * 60 + firstRun.getMinutes()
                )}.`
              : `Repeats every week, each day from its next ${formatTimeOfDay(startMinutes)}.`}
          </span>
        </p>

        {skipsToday ? (
          <p className="flex items-start gap-2 rounded-lg bg-orange-primary/10 px-3 py-2 text-xs text-bc-text">
            <CalendarDays className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-orange-primary" aria-hidden="true" />
            <span>
              {`Today's ${formatMinutesRange(startMinutes, endMinutes)} has already ended, so ${
                singleDay ? "this shift" : `the ${weekdayName(todayNumber)} shift`
              } starts next week, ${FROM_DATE(todayFirstRun)}. It won't show on today's row.`}
            </span>
          </p>
        ) : null}

        {/* "Pick at least one day" is not an error before anything is picked. */}
        {error || (problem && dayNumbers.length > 0) ? (
          <p role="alert" className="text-xs text-danger-dark">
            {error || problem}
          </p>
        ) : null}
      </div>
    </Modal>
  );
};

/**
 * Remove one shift **from now on**, whichever row its trash icon was on,
 * after asking. Always a close, never a DELETE (`planShiftRemoval`): a started
 * shift is closed now, so everything before keeps it; one that hasn't
 * started is closed a second after its own start, so it stays on record
 * but applies on no date. Since `bd2e283` the server refuses either while the
 * shift holds **any** upcoming booking, whatever the date (a 409); that is
 * shown as those calls in local time (`StrandedBookingsNotice`). Any other
 * refusal shows the API's own words.
 */
const RemoveShiftDialog = ({ shift, shifts, onClose, onDone }) => {
  const [openedAt] = useState(() => new Date());
  const [error, setError] = useState(null);
  const [stranded, setStranded] = useState(null);
  const { saving } = shifts;
  const { leaving, close, leaveThen } = useModalExit({ onClose, blocked: saving });

  // The same rule as `planShiftRemoval`.
  const started = !shift.effectiveFrom || shift.effectiveFrom < openedAt;
  const shiftLabel = `${weekdayName(shift.dayNumber)} ${formatMinutesRange(shift.startMinutes, shift.endMinutes)}`;

  // See ShiftFormDialog: blocks a second click before `saving` renders.
  const submittingRef = useRef(false);
  const remove = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setError(null);
    setStranded(null);
    try {
      await shifts.deleteShift(shift);
      leaveThen(() => onDone(`${shiftLabel} shift removed.`));
    } catch (err) {
      const calls = parseStrandedBookings(err?.message);
      if (calls.length) setStranded(calls);
      else setError(err?.message || "That didn't save. Try again.");
    } finally {
      submittingRef.current = false;
    }
  };

  return (
    <Modal
      isOpen
      onClose={close}
      animated
      leaving={leaving}
      closeDisabled={saving}
      title="Remove shift"
      subtitle={shiftLabel}
      footer={
        <>
          <button
            type="button"
            onClick={close}
            disabled={saving}
            className="min-h-[40px] rounded-lg border border-gray-200 px-4 text-sm font-semibold text-bc-text-2 hover:bg-light-gray/60 disabled:opacity-50"
          >
            Keep it
          </button>
          <button
            type="button"
            onClick={remove}
            disabled={saving}
            className="min-h-[40px] rounded-lg bg-danger-dark px-4 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            {saving ? "Removing…" : "Remove shift"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-bc-text-2">
          {started
            ? "Remove this shift from now on? Slots already past keep it."
            : "Remove this shift? It hasn't started yet, so it won't apply on any date."}
        </p>

        {stranded ? (
          <StrandedBookingsNotice bookings={stranded} />
        ) : null}

        {error ? (
          <p role="alert" className="text-xs text-danger-dark">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
};

/**
 * A refused shift removal, in words: which upcoming booked calls still hold
 * the shift, in local time. The server's 409 names each call by booking id and
 * UTC instant (`parseStrandedBookings` reads them back out). Since `bd2e283`
 * no removal date gets past it — a shift can't end at all while it holds an
 * upcoming call — so the only way on is to cancel or reschedule them.
 */
const StrandedBookingsNotice = ({ bookings }) => {
  const count = bookings.length;
  const when = (date) =>
    `${FROM_DATE(date)}, ${formatTimeOfDay(date.getHours() * 60 + date.getMinutes())}`;
  return (
    <div role="alert" className="space-y-2 rounded-lg border border-danger/30 bg-danger-bg px-3 py-2.5 text-xs text-danger-dark">
      <p className="font-semibold">
        {count === 1 ? "A booked call still holds" : `${count} booked calls still hold`} this shift:
      </p>
      <ul className="list-disc space-y-0.5 pl-4">
        {bookings.map((booking) => (
          <li key={booking.id}>{when(booking.startsAt)}</li>
        ))}
      </ul>
      <p className="text-bc-text-2">
        A shift can't end while it has upcoming calls — cancel or reschedule{" "}
        {count === 1 ? "it" : "these"} from the Bookings tab first.
      </p>
    </div>
  );
};

/**
 * The week button and its month popover: click a day to open the week it is
 * in. Closes on a pick, on Escape and on an outside click.
 */
const WeekPicker = ({ week, onPick }) => {
  const [open, setOpen] = useState(false);
  const anchor = week[0]?.date || new Date();
  const [view, setView] = useState({ year: anchor.getFullYear(), month: anchor.getMonth() });
  const wrapRef = useRef(null);

  // Re-anchor the popover on the week being shown each time it opens.
  useEffect(() => {
    if (open) setView({ year: anchor.getFullYear(), month: anchor.getMonth() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = (date) => {
    onPick(date);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="dialog"
        className="inline-flex min-h-[40px] items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-sm font-semibold text-bc-text transition-colors hover:bg-light-gray/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary"
      >
        <CalendarDays className="h-4 w-4 text-orange-primary" aria-hidden="true" />
        {formatWeekRange(week)}
        <ChevronDown className="h-4 w-4 text-bc-faint" aria-hidden="true" />
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Pick a week"
          className="absolute right-0 z-30 mt-2 w-[300px] rounded-xl border border-gray-200 bg-white p-3 shadow-lg"
        >
          <SlotMonthCalendar
            year={view.year}
            month={view.month}
            onMonthChange={(year, month) => setView({ year, month })}
            selectedKeys={week.map((day) => day.key)}
            onSelectDate={pick}
            markedByDate={{}}
            allowPast
            allowClosed
            showLegend={false}
          />
          <div className="mt-2 flex items-center justify-between text-[11px]">
            <span className="text-bc-soft">Pick a day to open that week</span>
            <button
              type="button"
              onClick={() => pick(new Date())}
              className="font-semibold text-orange-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary"
            >
              This week
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

/**
 * Every tier's slots on one day, as a client on that tier would be offered
 * them. Which tier a client actually sees is the server's call: their call
 * number in the department picks the tier (`callTierList`'s `appliesTo`), and
 * the last tier — the regular call — repeats for every later call.
 */
const ViewSlotsDialog = ({ day, dayShifts, bookings, shifts, onClose }) => {
  const { tiers, bufferByCallTypeId, offsetMinutes, departmentTitle } = shifts;
  const now = new Date();

  return (
    <Modal
      isOpen={Boolean(day)}
      onClose={onClose}
      size="lg"
      title={day ? `Slots · ${formatDayHeading(day.date)}` : ""}
      subtitle={
        day
          ? `Generated from ${dayShifts.length} shift${
              dayShifts.length === 1 ? "" : "s"
            }: ${dayShifts
              .map((shift) => formatMinutesRange(shift.startMinutes, shift.endMinutes))
              .join(", ")}`
          : ""
      }
    >
      {day ? (
        <div className="space-y-4">
          <p className="text-xs text-bc-soft">
            A preview of what clients can book. Each grid is the length a client's
            {departmentTitle ? ` ${departmentTitle}` : ""} call gets on that call number;
            the last one repeats for every call after it.
          </p>
          {callTierList(tiers).map(({ callNo, key, label, appliesTo }) => (
            <SlotPreviewList
              key={key}
              title={label}
              detail={`${appliesTo} · ${tiers[callNo].durationMinutes} min call · ${tiers[callNo].bufferMinutes} min buffer`}
              slots={shiftSlotsOnDate(day.date, dayShifts, tiers[callNo], bookings, {
                bufferByCallTypeId,
                offsetMinutes,
              })}
              now={now}
            />
          ))}
          <SlotPreviewLegend />
        </div>
      ) : null}
    </Modal>
  );
};
